import { useState, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { toast } from "sonner";
import { useGetAppointmentsQuery } from "@/queries/useGetAppointmentsQuery";
import { useGetClubsQuery } from "@/queries/useGetClubsQuery";
import { getClasses } from "@/http/coursesApi";
import { getCourseClassesQueryKey } from "@/utils/queryKeys";
import { computeClassesRange } from "@/queries/useGetCourseClassesQuery";
import { useDeleteClassesMutation } from "@/mutations/useDeleteClassesMutation";
import { useGetSameDayCancellationsQuery } from "@/queries/useGetSameDayCancellationsQuery";
import { useSameDayCancelActions } from "@/hooks/useSameDayCancelActions";
import { getCookie } from "@/utils/cookies";
import { useUserName } from "@/hooks/useUserName";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { formatDateWithTime } from "@/utils/dateFormatters";

export const useClassesInfo = () => {
  const usertoken: string | null = getCookie("user_token");
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [sameDayCancelDialog, setSameDayCancelDialog] = useState(false);
  const [deleteSameDayCancelDialog, setDeleteSameDayCancelDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedClass, setSelectedClass] = useState<Record<string, unknown> | null>(null);

  const userName = useUserName();
  const { authPhone } = useAuth();
  const { selectedClubId } = useClub();
  const { data: appointmentsData = [], refetch: refetchAppointments } = useGetAppointmentsQuery();
  const { data: clubsData } = useGetClubsQuery();
  const { data: sameDayCancellations = [], isLoading: isSameDayCancellationsLoading } =
    useGetSameDayCancellationsQuery();

  const { mutateAsync: deleteClassesMutate } = useDeleteClassesMutation();
  const { create: createSameDayCancel, remove: removeSameDayCancel } = useSameDayCancelActions();

  const clubNames = useMemo(() => {
    if (clubsData) {
      return clubsData.reduce((acc, club) => {
        acc[club.id] = club.title;

        return acc;
      }, {});
    }

    return {};
  }, [clubsData]);

  // В /appointments у групповых занятий course.title всегда null, а service.title —
  // это название услуги-категории (не то, что видно в расписании). Поэтому тянем
  // /classes/ для всех клубов с групповыми записями и маппим appointment_id -> course.title,
  // чтобы в «Ваши занятия» показывать то же имя, что и на /sign-up/groups.
  const groupClubIds = useMemo(
    () =>
      Array.from(
        new Set(
          appointmentsData
            .filter((a) => a.type === "classes")
            .map((a) => a.club_id)
            .filter(Boolean)
        )
      ),
    [appointmentsData]
  );

  // Единый диапазон с useGetCourseClassesQuery — один и тот же queryKey ⇒ кэш
  // шарится, нет дублирующих запросов между /sign-up/groups и «Ваши занятия».
  // TODO: когда бэк начнёт отдавать course.title в /appointments для type=group,
  // этот cross-lookup можно удалить целиком.
  const { startStr: classRangeStart, endStr: classRangeEnd } = computeClassesRange();

  const classesQueries = useQueries({
    queries: groupClubIds.map((clubId) => ({
      queryKey: [
        ...getCourseClassesQueryKey(clubId, classRangeStart, classRangeEnd),
        { userToken: usertoken },
      ],
      queryFn: async () => {
        const res = await getClasses(
          clubId,
          classRangeStart,
          classRangeEnd,
          usertoken || undefined
        );
        return res.data.data ?? [];
      },
      enabled: !!clubId,
      staleTime: 5 * 60_000,
    })),
  });

  const appointmentCourseTitles = useMemo(() => {
    const map = new Map<string, string>();
    classesQueries.forEach((q) => {
      (q.data ?? []).forEach((cls) => {
        const title = cls.course?.title;
        if (cls.appointment_id && title) map.set(cls.appointment_id, title);
      });
    });
    return map;
  }, [classesQueries]);

  const enrichedAppointments = useMemo(
    () =>
      appointmentsData.map((a) =>
        a.type === "classes" && appointmentCourseTitles.has(a.appointment_id)
          ? {
              ...a,
              service: {
                ...a.service,
                title: appointmentCourseTitles.get(a.appointment_id) ?? a.service?.title,
              },
            }
          : a
      ),
    [appointmentsData, appointmentCourseTitles]
  );

  const handleActions = {
    onClickDeleteClasses: (appointmentId: string) => {
      const selectedAppointment = appointmentsData.find(
        (app) => app.appointment_id === appointmentId
      );
      setSelectedAppointment(appointmentId);
      setSelectedClass(selectedAppointment);
      setDeleteDialog(true);
    },

    handleConfirmDelete: () => {
      const classInfo = selectedClass;
      const classClubName = classInfo?.club_id ? clubNames[classInfo.club_id as string] : "";
      const classDate = classInfo?.start_date
        ? formatDateWithTime(classInfo.start_date as string)
        : "";
      const instructorName = (classInfo?.employee as { name?: string })?.name || "";

      deleteClassesMutate({
        usertoken,
        appointment_id: selectedAppointment,
      })
        .then(() => {
          setDeleteDialog(false);
          setSelectedClass(null);
          refetchAppointments();
          toast.success("Занятие успешно отменено", {
            description: `${classDate} • АкваСтар ${classClubName}${instructorName ? ` • ${instructorName}` : ""}`,
          });
        })
        .catch((error) => {
          let message = "При отмене занятия произошла ошибка. Обратитесь к администратору.";
          if (error.response?.data?.error_message) {
            message = error.response.data.error_message;
          }
          setDeleteDialog(false);
          refetchAppointments();
          toast.error("Ошибка", { description: message });
        });
    },

    onClickSameDayCancel: (appointmentId: string) => {
      const appointment = appointmentsData.find((app) => app.appointment_id === appointmentId);
      setSelectedAppointment(appointmentId);
      setSelectedClass(appointment);
      setSameDayCancelDialog(true);
    },

    onDeleteSameDayCancel: (appointmentId: string) => {
      setSelectedAppointment(appointmentId);
      setDeleteSameDayCancelDialog(true);
    },

    handleConfirmDeleteSameDayCancel: async () => {
      if (!authPhone || !selectedAppointment) return;
      const cancellation = sameDayCancellations.find((c) => c.exercise_id === selectedAppointment);
      if (!cancellation) return;

      await removeSameDayCancel({ phone: authPhone, cancellationId: cancellation.id });
      setDeleteSameDayCancelDialog(false);
    },

    handleConfirmSameDayCancel: async () => {
      const classInfo = selectedClass;
      const classClubName = classInfo?.club_id ? clubNames[classInfo.club_id as string] : "";
      const classDate = classInfo?.start_date
        ? formatDateWithTime(classInfo.start_date as string)
        : "";

      if (!authPhone || !selectedAppointment) {
        toast.error("Не удалось определить номер телефона");
        return;
      }

      // Отменяемое занятие может быть в другом клубе (у юзера записи в нескольких).
      // Берём club_id из самого занятия; fallback на выбранный клуб — страховка.
      const cancelClubId = (classInfo?.club_id as string | undefined) ?? selectedClubId;

      await createSameDayCancel({
        phone: authPhone,
        clubId: cancelClubId,
        exerciseId: selectedAppointment,
        exerciseTitle: (classInfo?.service as { title?: string })?.title || "",
        exerciseTime: (classInfo?.start_date as string) || "",
        toastDescription: `${classDate} • АкваСтар ${classClubName}`,
      });
      setSameDayCancelDialog(false);
      setSelectedClass(null);
    },
  };

  return {
    state: {
      appointments: enrichedAppointments,
      clubNames,
      deleteDialog,
      sameDayCancelDialog,
      deleteSameDayCancelDialog,
      sameDayCancellations,
      isSameDayCancellationsLoading,
      userName,
    },
    actions: {
      setDeleteDialog,
      setSameDayCancelDialog,
      setDeleteSameDayCancelDialog,
      ...handleActions,
    },
  };
};
